import { Dispatch, SetStateAction } from 'react';
import axios from 'axios';
import { getApiUrl } from '../config/api';

export interface DownloadOptions {
  filename?: string;
  mimeType?: string;
  fileExtension?: string;
  setIsProcessing?: Dispatch<SetStateAction<boolean>>;
  timeout?: number;
  onStart?: () => void;
  onComplete?: () => void;
  onError?: (error: any) => void;
}

export interface MediaDownloadOptions extends DownloadOptions {
  type: 'audio' | 'video' | 'image' | 'document';
}

const getDefaultMimeType = (type: string): string => {
  switch (type) {
    case 'audio': return 'audio/wav';
    case 'video': return 'video/mp4';
    case 'image': return 'image/png';
    case 'document': return 'application/pdf';
    default: return 'application/octet-stream';
  }
};

const getDefaultExtension = (type: string): string => {
  switch (type) {
    case 'audio': return 'wav';
    case 'video': return 'mp4';
    case 'image': return 'png';
    case 'document': return 'pdf';
    default: return 'file';
  }
};

const getProxyEndpoint = (type: string): string => {
  switch (type) {
    case 'audio': return '/api/proxy-audio';
    case 'video': return '/api/proxy-video';
    case 'image': return '/api/proxy-image';
    default: return '/api/proxy-file';
  }
};

/**
 * Universal auto-download service for any file type
 * Handles authentication, S3 URLs, and automatic browser downloads
 */
const autoDownloadService = async (
  downloadUrl: string, 
  options: MediaDownloadOptions
): Promise<void> => {
  
  const {
    type,
    filename,
    mimeType = getDefaultMimeType(type),
    fileExtension = getDefaultExtension(type),
    setIsProcessing,
    timeout = 90000,
    onStart,
    onComplete,
    onError
  } = options;

  if (!downloadUrl) {
    console.error('❌ No download URL provided');
    const error = new Error('No download URL provided');
    if (onError) onError(error);
    return;
  }

  if (setIsProcessing) setIsProcessing(true);
  if (onStart) onStart();

  try {
    console.log(`🎯 Attempting ${type} download from:`, downloadUrl);
    
    // Check if this is a direct S3 URL - if so, use proxy approach
    if (downloadUrl.includes('amazonaws.com') || downloadUrl.includes('s3.')) {
      console.log('🔗 Direct S3 URL detected, using proxy approach');
      
      // Get authentication token for backend request
      const token = localStorage.getItem('authToken');
      const headers: Record<string, string> = {};
      
      // Add authorization header if token exists
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      // Use appropriate proxy endpoint based on file type
      const proxyEndpoint = getProxyEndpoint(type);
      const proxiedUrl = `${getApiUrl('')}${proxyEndpoint}?url=${encodeURIComponent(downloadUrl)}`;
      
      // Download through the proxy
      const response = await axios.get(proxiedUrl, {
        responseType: 'blob',
        headers,
        timeout
      });
      
      if (response.status === 200) {
        const blob = new Blob([response.data], { type: mimeType });
        triggerDownload(blob, filename || `${type}-${Date.now()}.${fileExtension}`);
        console.log(`✅ ${type} downloaded successfully via proxy`);
        if (onComplete) onComplete();
      }
    } else {
      // For backend URLs (like /uploads/...), use direct approach with auth headers
      const token = localStorage.getItem('authToken');
      const headers: Record<string, string> = {
        'Accept': mimeType,
        'Content-Type': mimeType
      };
      
      // Add authorization header if token exists
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      // Construct full URL if it's a relative path
      const fullUrl = downloadUrl.startsWith('http') ? downloadUrl : `${getApiUrl('')}${downloadUrl}`;
      
      const response = await axios.get(fullUrl, {
        responseType: 'blob',
        headers,
        timeout
      });
      
      if (response.status === 200) {
        const blob = new Blob([response.data], { type: mimeType });
        const extractedFilename = downloadUrl.split('/').pop()?.split('?')[0] || `${type}.${fileExtension}`;
        triggerDownload(blob, filename || extractedFilename);
        console.log(`✅ ${type} downloaded successfully`);
        if (onComplete) onComplete();
      }
    }
    
  } catch (error) {
    console.error(`❌ ${type} download failed:`, {
      error,
      url: downloadUrl,
      status: axios.isAxiosError(error) ? error.response?.status : 'Unknown error',
      type
    });
    if (onError) onError(error);
    throw error; // Re-throw so caller can handle the error
  } finally {
    if (setIsProcessing) setIsProcessing(false);
  }
};

/**
 * Helper function to trigger browser download
 */
const triggerDownload = (blob: Blob, filename: string): void => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  
  document.body.appendChild(a);
  a.click();
  
  // Cleanup
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
};

/**
 * Convenience functions for specific file types
 */
export const downloadAudio = (url: string, options?: Omit<DownloadOptions, 'type'>) => 
  autoDownloadService(url, { type: 'audio', ...options });

export const downloadVideo = (url: string, options?: Omit<DownloadOptions, 'type'>) => 
  autoDownloadService(url, { type: 'video', ...options });

export const downloadImage = (url: string, options?: Omit<DownloadOptions, 'type'>) => 
  autoDownloadService(url, { type: 'image', ...options });

export const downloadFile = (url: string, options: MediaDownloadOptions) => 
  autoDownloadService(url, options);

export default autoDownloadService;